// Sandboxed rendering for HTML document pages (review 10-07 of the C-CDA viewer).
//
// Every HTML page the document viewers get from the gateway is third-party content: Athena's
// documentdata (e.g. a Direct cover note written by an outside sender), a discharge summary, or
// the gateway's own sanitized C-CDA render. None of it may run in the care origin, so it is
// never injected into the app DOM (no dangerouslySetInnerHTML) and never opened from a blob: URL
// (a blob: URL inherits the care origin). It goes into an iframe via srcdoc with
//   sandbox="allow-same-origin"  - no allow-scripts / forms / popups / top-navigation; same-origin
//                                  only so the viewer can read the height to size the frame
//   a CSP meta                   - default-src 'none': no script, no network fetch (images only
//                                  as data: URIs), so a page cannot beacon out either.
import { useMemo, useRef, useState } from "react";

export const LOCKDOWN_CSP = "default-src 'none'; style-src 'unsafe-inline'; img-src data:";

// base64 -> UTF-8 text (atob alone yields Latin-1 mojibake for "–", "é", ...)
export const decodeUtf8B64 = (b64) => {
    const bin = atob(b64 || "");
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    try {
        if (typeof TextDecoder !== "undefined") return new TextDecoder("utf-8").decode(bytes);
        return decodeURIComponent(escape(bin)); // older engines / jsdom
    } catch (e) {
        return bin;
    }
};

// Prefix the lockdown CSP. A leading <meta> lands in the implied <head>, where it applies; the
// page's own DOCTYPE is moved in front so the frame stays in standards mode.
const LEADING_DOCTYPE = /^\uFEFF?\s*<!doctype[^>]*>/i;
// BASE_STYLE matches the old inline rendering (14px / 1.6 / #333, 24px padding).
const BASE_STYLE =
    "body{margin:0;padding:24px;background:#fff;color:#333;font:14px/1.6 -apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;overflow-wrap:anywhere}" +
    "img{max-width:100%}table{max-width:100%}";
export const withLockdownCsp = (html) =>
    `<!DOCTYPE html><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${LOCKDOWN_CSP}">` +
    `<base target="_self"><style>${BASE_STYLE}</style>${String(html || "").replace(LEADING_DOCTYPE, "")}`;

// kind: "ccda" (gateway render, already carries its own CSP) | "html" (any other HTML page).
// fill: take the parent's full height instead of sizing to the content.
export const SandboxedHtmlFrame = ({ data, index = 0, kind = "html", fill = false, title }) => {
    const ref = useRef(null);
    const [height, setHeight] = useState(640);
    const srcDoc = useMemo(() => {
        let html = "";
        try {
            html = decodeUtf8B64(data);
        } catch (e) {
            html = "";
        }
        return withLockdownCsp(html);
    }, [data]);
    const fit = () => {
        if (fill) return;
        try {
            const d = ref.current && ref.current.contentDocument;
            const h = d && ((d.documentElement && d.documentElement.scrollHeight) || (d.body && d.body.scrollHeight));
            if (h) setHeight(Math.min(Math.max(h + 24, 200), 20000));
        } catch (e) {
            /* keep the default height */
        }
    };
    const label = title || (kind === "ccda" ? `Care summary (C-CDA) ${index + 1}` : `Document page ${index + 1}`);
    return (
        <iframe
            ref={ref}
            data-testid={kind === "ccda" ? "ccda-frame" : "html-frame"}
            title={label}
            sandbox="allow-same-origin"
            referrerPolicy="no-referrer"
            srcDoc={srcDoc}
            onLoad={fit}
            className={fill ? "w-full h-full border-0 bg-white" : "w-full rounded-lg border border-gray-200 bg-white"}
            style={fill ? undefined : { height }}
        />
    );
};

// Empty-state text for a received C-CDA, by the gateway's no_content_reason (chart viewer and
// tasks card).
export const ccdaEmptyMessage = (reason) => {
    const where = "open it in athenaNet (Clinical Documents) to read it.";
    switch (reason) {
        case "ccda_xml_not_requested":
            return `This care summary was received electronically from another organization. Its content comes only from a paid Athena request, which is switched off for this practice, so it is not shown here yet — ${where}`;
        case "ccda_xml_not_licensed":
            return `Athena declined to return this care summary's content to our API account (not covered by the current Athena API agreement) — ${where}`;
        case "ccda_xml_absent":
            return `Athena returned no readable content for this care summary — ${where}`;
        case "ccda_unreadable":
            return `This care summary's content could not be displayed safely here — ${where}`;
        case "ccda_xml_refused":
            return `Athena refused the request for this care summary's content — ${where}`;
        case "ccda_xml_error":
            return `Athena did not answer the request for this care summary. Try again later, or ${where}`;
        default:
            return `This is a care record received electronically from another organization. It cannot be displayed here — ${where}`;
    }
};

// How a document page may be shown (review 10-07n finding 3). Content types reach the viewers as
// Athena / FHIR sent them, so only the base type counts, case-insensitively, and only a real PDF
// ever gets a blob: URL (which runs in the care origin) - always typed application/pdf. HTML and
// SVG go to the sandboxed frame, raster images to <img>, anything else is shown as plain text.
// A generic binary type (Athena's originaldocument without a contenttype) is sniffed for a PDF or
// a raster image by its first bytes. Returns { kind: "pdf" | "html" | "image" | "text", mime }.
const GENERIC_BINARY = new Set(["", "application/octet-stream", "binary/octet-stream", "application/binary", "application/unknown"]);
const B64_MAGIC = [
    ["JVBERi", "pdf", "application/pdf"],
    ["iVBORw0KGgo", "image", "image/png"],
    ["/9j/", "image", "image/jpeg"],
    ["R0lGOD", "image", "image/gif"],
];
export const docPageKind = (contentType, b64) => {
    const base = String(contentType || "").split(";")[0].trim().toLowerCase();
    if (base === "application/pdf" || base === "application/x-pdf") return { kind: "pdf", mime: "application/pdf" };
    if (base.includes("html") || base === "image/svg+xml") return { kind: "html", mime: base };
    if (base.startsWith("image/") && /^image\/[a-z0-9.+-]+$/.test(base)) return { kind: "image", mime: base };
    if (GENERIC_BINARY.has(base)) {
        const head = String(b64 || "").slice(0, 16);
        const hit = B64_MAGIC.find(([magic]) => head.startsWith(magic));
        if (hit) return { kind: hit[1], mime: hit[2] };
        if (!base) return { kind: "pdf", mime: "application/pdf" }; // no type at all: the old PDF default
    }
    return { kind: "text", mime: "text/plain" };
};

export default SandboxedHtmlFrame;
